/**
 * UI-6b Task 14 — Widget library sidebar.
 *
 * Lists every registered widget manifest (label + description) as draggable
 * <li>s. Drag-source contract: writes the widget `type` to a custom MIME
 * type `application/widget-type` on the DataTransfer; the composer canvas
 * (Task 17) reads that key in its onDrop handler.
 *
 * Why a custom MIME type: keeps the payload typed so other drag sources
 * (file uploads, etc.) won't accidentally trigger a widget-drop, and avoids
 * polluting `text/plain`.
 */
import type { DragEvent, JSX } from 'react'
import { WIDGET_REGISTRY } from './widgets/index.js'

export const WIDGET_DRAG_MIME = 'application/widget-type'

export default function WidgetLibrarySidebar(): JSX.Element {
  const manifests = Object.values(WIDGET_REGISTRY)
  return (
    <aside className="w-56 shrink-0 border-r p-3" aria-label="Widget library">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
        Widgets
      </h3>
      <ul className="mt-2 space-y-1">
        {manifests.map((m) => (
          <li
            key={m.type}
            draggable
            onDragStart={(e: DragEvent<HTMLLIElement>): void => {
              e.dataTransfer.setData(WIDGET_DRAG_MIME, m.type)
              e.dataTransfer.effectAllowed = 'copy'
            }}
            className="cursor-grab rounded border p-2 text-sm hover:bg-gray-50"
            aria-label={`Drag ${m.label} widget`}
            data-widget-type={m.type}
          >
            <div className="font-medium">{m.label}</div>
            <div className="text-xs text-gray-500">{m.description}</div>
          </li>
        ))}
      </ul>
    </aside>
  )
}
